import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservationService } from '../../services/reservation.service'; 
import { FormsModule, FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { HostingService } from '../../services/hosting.service';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { TokenStorageService } from '../../services/token-storage.service';

interface Guest {
  name: string;
  lastName: string;
  idType: string;
  idNumber: string;
  phone: string;
  emergencyPhone: string;
}

interface AdditionalService {
  id: string;
  service: string;
  cost: number;
  status: string;
  selected: boolean;
}

@Component({
  selector: 'app-order-summary',
  imports: [
    FormsModule,
    CommonModule,
    NavbarComponent,
    FooterComponent,
    ReactiveFormsModule
  ],
  templateUrl: './order-summary.component.html',
  styleUrl: './order-summary.component.css'
})
export class OrderSummaryComponent implements OnInit {
  reservationSummary: any;
  guestForm: FormGroup;
  guests: any[] = [];
  hosting: any; 
  additionalServicesDetails: any[] = []; // Detalles de los servicios adicionales
  guestprueba: any;
  showSuccess: boolean = false;
  isLoading: boolean = false;
  currentUserAttributes: Record<string, string> | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly hostingService: HostingService,
    private readonly reservationService: ReservationService,
    private readonly authService: AuthService,
    private readonly tokenStorage: TokenStorageService
  ) {
    this.guestForm = this.fb.group({
      guests: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadCurrentUser().then(() => {
      // Intentar obtener el estado de la navegación
      const navigation = this.router.getCurrentNavigation();
      let state = navigation?.extras?.state as { 
        reservationData: any; 
        hosting: any 
      };
    
      // Si no hay estado, verificar localStorage
      if (!state) {
        const savedData = localStorage.getItem('tempReservationData');
        if (savedData) {
          state = JSON.parse(savedData);
          localStorage.removeItem('tempReservationData'); // Limpiar después de usar
        }
      }
    
      console.log('Estado recibido:', state); // Depuración
    
      if (state?.reservationData && state?.hosting) {
        this.reservationSummary = state.reservationData;
        this.hosting = state.hosting;
        this.initializeGuests();
        this.filterAdditionalServices(this.reservationSummary.additionalServices);
      }
    });
  }
  // Obtener detalles del alojamiento
  fetchHostingDetails(id: string) {
    this.hostingService.getHosting(id).subscribe({
      next: (response: any) => {
        this.hosting = response;
        this.filterAdditionalServices(this.reservationSummary.additionalServices); // Filtrar servicios adicionales
      },
      error: (error) => {
        console.error('Error fetching hosting details:', error);
      }
    });
  }

  // Filtrar servicios adicionales basados en los IDs de la reserva
  filterAdditionalServices(reservationServices: any[]) {
    if (!this.hosting?.additional) {
      console.error('Hosting data not loaded or missing additional services');
      return;
    }
  
    // Paso 1: Extraer solo los IDs de los servicios en la reserva
    const reservationServiceIds = reservationServices.map(service => service.serviceId);
  
    // Paso 2: Filtrar los servicios del hosting que coincidan con los IDs de la reserva
    this.additionalServicesDetails = this.hosting.additional
      .filter((hostingService: any) => 
        reservationServiceIds.includes(hostingService.additional_id)
      )
      .map((matchedService: any) => ({
        id: matchedService.additional_id,
        name: matchedService.service,
        cost: Number(matchedService.cost)
      }));
  
    console.log('Servicios adicionales filtrados:', this.additionalServicesDetails);
  }

  private async loadCurrentUser() {
    try {
      this.currentUserAttributes = await this.authService.getUser();
      console.log('✅ Atributos del usuario desde servidor:', this.currentUserAttributes); 
    } catch (error) {
      console.warn('⚠️ Error obteniendo datos del servidor, usando datos del token local:', error);
      // Fallback: usar datos del token local
      const userFromToken = this.authService.getUserFromToken();
      if (userFromToken) {
        this.currentUserAttributes = {
          'name': userFromToken['given_name'] || '',
          'family_name': userFromToken['family_name'] || '',
          'email': userFromToken['email'] || '',
          'email_verified': userFromToken['email_verified'] ? 'true' : 'false'
        };
        console.log('🔄 Usando datos del token local:', this.currentUserAttributes);
      } else {
        console.error('❌ No se pudo obtener datos del usuario');
        this.currentUserAttributes = null;
      }
    }
  }
  
  // Inicializar el formulario de huéspedes
  initializeGuests() {
    const numGuests = this.reservationSummary.numAdults + this.reservationSummary.numChildren + this.reservationSummary.numInfants;
    
    // Huésped 1 con datos del usuario
    const firstGuest = this.fb.group({
      name: [this.currentUserAttributes?.['name'] || '', Validators.required],
      lastname: [this.currentUserAttributes?.['family_name'] || '', Validators.required],
      identificationType: [this.currentUserAttributes?.['custom:document_type'] || 'cedula-ciudadania', Validators.required],
      identificationNumber: [this.currentUserAttributes?.['custom:document_number'] || '', Validators.required],
      phone: [this.currentUserAttributes?.['phone_number'] || '', Validators.required],
      emergencyPhone: ['', Validators.required], 
      reservation_id: this.reservationSummary.reservation_id
    });

    this.guests.push(firstGuest);
    (this.guestForm.get('guests') as FormArray).push(firstGuest);

    // Resto de huéspedes
    for (let i = 1; i < numGuests; i++) {
      this.addGuest();
    }
  }

  // Agregar un huésped al formulario
  addGuest() {
    const guestGroup = this.fb.group({
      name: ['', Validators.required],
      lastname: ['', Validators.required],
      identificationType: ['cedula-ciudadania', Validators.required],
      identificationNumber: ['', Validators.required],
      phone: ['', Validators.required],
      emergencyPhone: ['', Validators.required],
      reservation_id: this.reservationSummary.reservation_id
    });
    this.guests.push(guestGroup);
    (this.guestForm.get('guests') as FormArray).push(guestGroup);
  }

  // Enviar los datos de los huéspedes 
  onSubmit() {
    if (this.guestForm.valid) {
      this.isLoading = true;
      const guestsData = this.guestForm.value.guests;
      
      // Transformar los datos al formato esperado por el backend
      const modifiedGuests = guestsData.map((guest: any) => ({
        name: guest.name,
        lastname: guest.lastname,
        identificationType: guest.identificationType,
        identificationNumber: guest.identificationNumber,
        phone: guest.phone,
        emergencyPhone: guest.emergencyPhone,
        reservation_id: this.reservationSummary.reservation_id
      }));
  
      console.log('Enviando huéspedes:', modifiedGuests);
      
      this.reservationService.addGuests(modifiedGuests).subscribe({
        next: (response: any) => {
          console.log('Huéspedes registrados exitosamente:', response);
          this.isLoading = false;
          this.showSuccess = true;
          
          // Mostrar mensaje de éxito por 3 segundos y luego redirigir al dashboard
          setTimeout(() => {
            this.router.navigate(['/my-reservations']);
          }, 3000);
        },
        error: (error) => {
          console.error('Error al guardar huéspedes:', error);
          this.isLoading = false;
          alert('Error al registrar los huéspedes: ' + (error.error?.message || error.message));
        }
      });
    } else {
      alert('Por favor completa todos los campos requeridos');
    }
  }

  fetchData() {
    const accessToken = this.tokenStorage.getAccessToken();
    if (!accessToken || !/^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(accessToken)) {
      alert('Error: Token de acceso inválido o ausente. Por favor inicia sesión nuevamente.');
      this.router.navigate(['/loggin']);
      return;
    }
    this.reservationService.getGuests('c318fce2-f364-4432-a340-e6d6c3b25216', accessToken).subscribe(response => {
      this.guestprueba = response;
      console.log('Datos recibidos:', this.guestprueba);
    });
  }
  
  //Función para volver a atras y eliminar todos los datos de la reserva actual
  async cancelAndEditReservation() {
    if (!this.reservationSummary) return;

    // 1. Guarda los datos críticos ANTES de eliminar
    const hostingId = this.reservationSummary.hotelId; 
    const navigationState = {
      checkInDate: this.reservationSummary.checkInDate,
      checkOutDate: this.reservationSummary.checkOutDate,
      numAdults: this.reservationSummary.numAdults,
      numChildren: this.reservationSummary.numChildren,
      numInfants: this.reservationSummary.numInfants,
      selectedServices: this.reservationSummary.additionalServices.map((service: AdditionalService) => service.id)
    };

    if (confirm('¿Estás seguro de que quieres modificar esta reserva? Se cancelará la reserva actual y podrás crear una nueva.')) {
      try {
        // Obtener token de acceso
        const accessToken = this.tokenStorage.getAccessToken();
        
        if (!accessToken) {
          alert('Error de autenticación. Por favor inicia sesión nuevamente.');
          this.router.navigate(['/loggin']);
          return;
        }

        // 2. Eliminar la reserva usando el token
        await firstValueFrom(this.reservationService.deleteReservation(this.reservationSummary.reservation_id, accessToken));
        
        // 3. Redirigir CON los datos guardados
        this.router.navigate(['/hosting-detail', hostingId], { 
          state: navigationState 
        }).then((navSuccess: boolean) => {
          if (!navSuccess) {
            this.router.navigate(['/']); // Fallback
          }
        });
        
      } catch (error: any) {
        console.error('Error eliminando reserva:', error);
        alert('No se pudo cancelar la reserva: ' + (error.error?.message || error.message));
      }
    }
  }

  /**
   * Obtener clase CSS para el estado de la reserva
   */
  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'Pendiente de pago': 'status-pending',
      'Reserva confirmada - Pendiente de pago': 'status-confirmed-pending',
      'Esperando pago': 'status-waiting-payment',
      'Pagado': 'status-paid',
      'Pago rechazado': 'status-rejected',
      'Cancelado': 'status-cancelled'
    };

    return statusClasses[status] || 'status-default';
  }
}
